import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button, Form, Alert, Table, Badge } from 'react-bootstrap';
import { GoogleMap, useJsApiLoader, DirectionsRenderer, Marker } from '@react-google-maps/api';
import type { Library } from '@googlemaps/js-api-loader';
import { Booking } from '../booking';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import Swal from 'sweetalert2';

interface MergeSuggestion {
  groupId: string;
  bookings: Booking[] | null | undefined;
  vehicleSuggestions: string[];
  driverSuggestions: string[] | null;
  suggestedDepartureTime: string | null;
  suggestedArrivalTime: string | null;
  comment: string | null;
}

interface Driver {
  empNumber: string;
  name: string;
}

interface Vehicle {
  numberPlate: string;
  model: string;
}

interface RawDriverData {
  empNumber: string | number;
  name: string;
  email?: string;
  phone?: string;
}

interface RawVehicleData {
  vehicleId?: string | number;
  numberPlate?: string | number;
  vehicleModel?: string;
  model?: string;
}

interface ViewMergeBookingProps {
  show: boolean;
  onHide: () => void;
  mergeGroup: MergeSuggestion | null;
  statusColors: { [key: string]: string };
  handleAcceptMerge: (groupId: string, numberPlate: string | null, empNumber: string | null) => Promise<boolean>;
  handleRejectMerge: (groupId: string, comment: string) => Promise<boolean>;
  isMerging: boolean;
}

const GOOGLE_MAPS_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
  'AIzaSyC7S0DKmKpZmtidyMto2PiIXhR5miu9Pi0';
const GOOGLE_MAPS_LIBRARIES: Library[] = ['places'];
const center = { lat: 6.9271, lng: 79.8612 }; // Default center (Colombo, Sri Lanka)

const ViewMergeBooking: React.FC<ViewMergeBookingProps> = ({
  show,
  onHide,
  mergeGroup,
  statusColors,
  handleAcceptMerge,
  handleRejectMerge,
  isMerging,
}) => {
  const [selectedVehicle, setSelectedVehicle] = useState<string>('');
  const [selectedDriver, setSelectedDriver] = useState<string>('');
  const [rejectComment, setRejectComment] = useState<string>('');
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isLoadingDrivers, setIsLoadingDrivers] = useState<boolean>(false);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const tripOneMapRef = useRef<google.maps.Map | null>(null);
  const tripTwoMapRef = useRef<google.maps.Map | null>(null);
  const [tripOneDirections, setTripOneDirections] = useState<google.maps.DirectionsResult | null>(null);
  const [tripTwoDirections, setTripTwoDirections] = useState<google.maps.DirectionsResult | null>(null);

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const formatDate = (input: number | string | null | undefined): string => {
    if (!input) return 'N/A';
    let date: Date;
    if (typeof input === 'number') {
      date = new Date(input);
    } else {
      date = new Date(Number(input));
    }
    if (isNaN(date.getTime())) {
      return 'Invalid Date';
    }
    return date.toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  };

  const splitTrips = (bookings: Booking[] | null | undefined): { tripOne: Booking[]; tripTwo: Booking[] } => {
    if (!Array.isArray(bookings) || bookings.length === 0) {
      return { tripOne: [], tripTwo: [] };
    }
    const sortedBookings = [...bookings].sort((a, b) => Number(a.departureTime) - Number(b.departureTime));
    if (sortedBookings.length === 0) {
      return { tripOne: [], tripTwo: [] };
    }
    const earliestDeparture = sortedBookings[0].departureTime;
    const tripOne = sortedBookings.filter((booking) => booking.departureTime === earliestDeparture);
    const tripTwo = sortedBookings.filter((booking) => booking.departureTime !== earliestDeparture);
    return { tripOne, tripTwo };
  };

  const renderDestinations = (locations: Booking['locations']): JSX.Element => {
    if (!locations || locations.length === 0) {
      return <tr><td colSpan={2}>No destinations available</td></tr>;
    }
    return (
      <>
        {locations.map((location, index) => (
          <tr key={`${location.id}-${index}`}>
            <td>{index + 1}</td>
            <td
              title={location.address || 'N/A'}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '250px',
              }}
            >
              {location.address
                ? location.address.length > 40
                  ? `${location.address.slice(0, 40)}...`
                  : location.address
                : 'N/A'}
            </td>
          </tr>
        ))}
      </>
    );
  };

  const calculateRoute = useCallback(
    async (bookings: Booking[], mapRef: React.MutableRefObject<google.maps.Map | null>, setDirections: (result: google.maps.DirectionsResult | null) => void) => {
      if (!isLoaded || !bookings || bookings.length === 0) {
        return;
      }
      const locations = bookings.flatMap((booking) => booking.locations || []).filter((loc) => loc.lat && loc.lng);
      if (locations.length < 2) {
        return;
      }
      const directionsService = new google.maps.DirectionsService();
      const waypoints = locations
        .slice(1, -1)
        .map((loc) => ({
          location: new google.maps.LatLng(parseFloat(loc.lat), parseFloat(loc.lng)),
          stopover: true,
        }));
      const origin = locations[0];
      const destination = locations[locations.length - 1];
      if (!origin || !destination || !origin.lat || !origin.lng || !destination.lat || !destination.lng) {
        return;
      }
      try {
        const result = await directionsService.route({
          origin: new google.maps.LatLng(parseFloat(origin.lat), parseFloat(origin.lng)),
          destination: new google.maps.LatLng(parseFloat(destination.lat), parseFloat(destination.lng)),
          waypoints,
          travelMode: google.maps.TravelMode.DRIVING,
          optimizeWaypoints: true,
        });
        setDirections(result);
        if (mapRef.current && result.routes[0]) {
          const bounds = new google.maps.LatLngBounds();
          result.routes[0].overview_path.forEach((point) => bounds.extend(point));
          mapRef.current.fitBounds(bounds);
        }
      } catch (error: any) {
        setError('Failed to calculate route for map.');
      }
    },
    [isLoaded]
  );

  const getTotalEstimatedKMs = (bookings: Booking[] | null | undefined): number => {
    if (!Array.isArray(bookings)) {
      return 0;
    }
    const total = bookings.reduce((sum, booking) => sum + (Number(booking.estimatedKms) || 0), 0);
    return total;
  };

  const getTotalPassengers = (bookings: Booking[] | null | undefined): number => {
    if (!Array.isArray(bookings)) {
      return 0;
    }
    const total = bookings.reduce((sum, booking) => sum + (Number(booking.noOfPassengers) || 0), 0);
    return total;
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!show || !mergeGroup) {
        setError('No merge group provided.');
        return;
      }

      if (!Array.isArray(mergeGroup.bookings)) {
        setError('Invalid or missing bookings data in merge group.');
        Swal.fire({
          icon: 'error',
          title: 'Invalid Merge Group',
          text: 'Bookings data is missing or invalid for this merge group.',
        });
        return;
      }

      if (mergeGroup.bookings.length === 0) {
        setError('No bookings available in this merge group.');
        Swal.fire({
          icon: 'warning',
          title: 'No Bookings',
          text: 'No bookings are available in this merge group.',
        });
        return;
      }

      setIsLoadingDrivers(true);
      setIsLoadingVehicles(true);
      setError(null);

      try {
        let suggestedDeparture = mergeGroup.suggestedDepartureTime ? Number(mergeGroup.suggestedDepartureTime) : null;
        let suggestedArrival = mergeGroup.suggestedArrivalTime ? Number(mergeGroup.suggestedArrivalTime) : null;
        const totalPassengers = getTotalPassengers(mergeGroup.bookings);

        if (!suggestedDeparture || !suggestedArrival || isNaN(suggestedDeparture) || isNaN(suggestedArrival)) {
          const departureTimes = mergeGroup.bookings
            .map((booking) => Number(booking.departureTime))
            .filter((time) => !isNaN(time));
          const arrivalTimes = mergeGroup.bookings
            .map((booking) => Number(booking.arrivalTime))
            .filter((time) => !isNaN(time));

          if (departureTimes.length === 0 || arrivalTimes.length === 0) {
            setError('Invalid departure or arrival times in merge group bookings.');
            Swal.fire({
              icon: 'warning',
              title: 'Invalid Time Range',
              text: 'One or more bookings have invalid departure or arrival times.',
            });
            setDrivers([]);
            setVehicles([]);
            setIsLoadingDrivers(false);
            setIsLoadingVehicles(false);
            return;
          }

          suggestedDeparture = Math.min(...departureTimes);
          suggestedArrival = Math.max(...arrivalTimes);
        }

        if (totalPassengers === 0) {
          setError('No passengers specified in the merge group.');
          Swal.fire({
            icon: 'warning',
            title: 'No Passengers',
            text: 'No passengers are specified for this merge group.',
          });
          setVehicles([]);
          setIsLoadingVehicles(false);
          return;
        }

        const departureTimeStr = String(suggestedDeparture);
        const arrivalTimeStr = String(suggestedArrival);


        const driverResponse = await apiDefinitions.getAvailableDrivers(departureTimeStr, arrivalTimeStr);

        if (driverResponse.data.status !== 'S1000') {
          setError(driverResponse.data.statusDescription || `Error fetching drivers for merge group ${mergeGroup.groupId}.`);
          Swal.fire({
            icon: 'error',
            title: 'API Error',
            text: driverResponse.data.statusDescription || `Failed to fetch drivers for merge group ${mergeGroup.groupId}.`,
          });
          setDrivers([]);
        } else {
          const driverData: RawDriverData[] = driverResponse.data?.data?.drivers || [];
          if (!Array.isArray(driverData)) {
            setError('Invalid driver data format.');
            Swal.fire({
              icon: 'warning',
              title: 'Invalid Driver Data',
              text: 'Driver data is not in the expected format.',
            });
            setDrivers([]);
          } else if (driverData.length === 0) {
            setError('No drivers available for this merge group.');
            Swal.fire({
              icon: 'warning',
              title: 'No Drivers Found',
              text: 'No drivers are available for this merge group.',
            });
            setDrivers([]);
          } else {
            const mappedDrivers = driverData
              .map((d: RawDriverData) => ({
                empNumber: String(d.empNumber),
                name: d.name || 'Unknown',
              }))
              .filter((d: Driver) => d.empNumber && d.empNumber !== '');
            const filteredDrivers = Array.isArray(mergeGroup.driverSuggestions) && mergeGroup.driverSuggestions.length > 0
              ? mappedDrivers.filter((driver) => mergeGroup.driverSuggestions!.includes(driver.empNumber))
              : mappedDrivers;
            setDrivers(filteredDrivers);
            if (filteredDrivers.length === 0 && Array.isArray(mergeGroup.driverSuggestions) && mergeGroup.driverSuggestions.length > 0) {
              setError('No matching drivers found for the provided suggestions.');
              Swal.fire({
                icon: 'warning',
                title: 'No Matching Drivers',
                text: 'No drivers match the provided suggestions.',
              });
            }
          }
        }
      } catch (err: any) {
        const errorMsg = err.message || 'Failed to fetch drivers.';
        setError(errorMsg);
        Swal.fire({
          icon: 'error',
          title: 'Fetch Error',
          text: errorMsg,
        });
      } finally {
        setIsLoadingDrivers(false);
      }

      try {
  let suggestedDeparture = mergeGroup.suggestedDepartureTime ? Number(mergeGroup.suggestedDepartureTime) : null;
  let suggestedArrival = mergeGroup.suggestedArrivalTime ? Number(mergeGroup.suggestedArrivalTime) : null;
  const totalPassengers = getTotalPassengers(mergeGroup.bookings);



  if (!suggestedDeparture || !suggestedArrival || isNaN(suggestedDeparture) || isNaN(suggestedArrival)) {
    const departureTimes = mergeGroup.bookings
      .map((booking) => Number(booking.departureTime))
      .filter((time) => !isNaN(time));
    const arrivalTimes = mergeGroup.bookings
      .map((booking) => Number(booking.arrivalTime))
      .filter((time) => !isNaN(time));


    if (departureTimes.length === 0 || arrivalTimes.length === 0) {
      setError('Invalid departure or arrival times in merge group bookings.');
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Time Range',
        text: 'One or more bookings have invalid departure or arrival times.',
      });
      setVehicles([]);
      setIsLoadingVehicles(false);
      return;
    }

    suggestedDeparture = Math.min(...departureTimes);
    suggestedArrival = Math.max(...arrivalTimes);
  }

  if (totalPassengers === 0) {
    setError('No passengers specified in the merge group.');
    Swal.fire({
      icon: 'warning',
      title: 'No Passengers',
      text: 'No passengers are specified for this merge group.',
    });
    setVehicles([]);
    setIsLoadingVehicles(false);
    return;
  }

  const vehicleResponse = await apiDefinitions.getAvailableVehicles(
    suggestedDeparture,
    suggestedArrival,
    totalPassengers
  );

  const vehicleData: RawVehicleData[] = vehicleResponse.data?.data?.vehicles || [];

  if (!Array.isArray(vehicleData) || vehicleData.length === 0) {
    setError('No vehicles found for the specified time and passenger count.');
    Swal.fire({
      icon: 'warning',
      title: 'No Vehicles Found',
      text: 'No vehicles are available for the specified time and passenger count.',
    });
    setVehicles([]);
  } else {
    const mappedVehicles = vehicleData
      .map((v: RawVehicleData) => ({
        numberPlate: String(v.numberPlate || ''),
        model: v.vehicleModel || v.model || 'N/A',
      }))
      .filter((v: Vehicle) => v.numberPlate && v.numberPlate !== '');

    let filteredVehicles = mappedVehicles;
    if (Array.isArray(mergeGroup.vehicleSuggestions) && mergeGroup.vehicleSuggestions.length > 0) {
      filteredVehicles = mappedVehicles.filter((vehicle) => mergeGroup.vehicleSuggestions.includes(vehicle.numberPlate));
      if (filteredVehicles.length === 0) {
        filteredVehicles = mappedVehicles;
      }
    } else {
    }
    setVehicles(filteredVehicles);
  }
} catch (err: any) {
  const errorMsg = err.message || 'Failed to fetch vehicles.';
  setError(errorMsg);
  Swal.fire({
    icon: 'error',
    title: 'Fetch Error',
    text: errorMsg,
  });
} finally {
  setIsLoadingVehicles(false);
}
    };

    if (show) {
      fetchData();
    }
  }, [show, mergeGroup]);

  useEffect(() => {
    if (show && isLoaded && mergeGroup && Array.isArray(mergeGroup.bookings)) {
      const { tripOne, tripTwo } = splitTrips(mergeGroup.bookings);
      if (tripOne.length > 0) {
        calculateRoute(tripOne, tripOneMapRef, setTripOneDirections);
      }
      if (tripTwo.length > 0) {
        calculateRoute(tripTwo, tripTwoMapRef, setTripTwoDirections);
      }
    }
  }, [show, isLoaded, mergeGroup, calculateRoute]);

  const handleAccept = async () => {
    if (!selectedVehicle || !selectedDriver) {
      setError('Please select a vehicle and a driver.');
      Swal.fire({
        icon: 'error',
        title: 'Missing Selection',
        text: 'Please select both a vehicle and a driver before accepting.',
      });
      return;
    }

    if (!mergeGroup?.groupId) {
      setError('Invalid merge group ID.');
      Swal.fire({
        icon: 'error',
        title: 'Invalid Merge Group',
        text: 'Merge group ID is missing.',
      });
      return;
    }

    setError(null);
    try {
      const success = await handleAcceptMerge(mergeGroup.groupId, selectedVehicle, selectedDriver);
      if (success) {
        setSelectedVehicle('');
        setSelectedDriver('');
        onHide();
      }
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to accept merge group.';
      setError(errorMsg);
      Swal.fire({
        icon: 'error',
        title: 'Merge Accept Error',
        text: errorMsg,
      });
    }
  };

  const handleReject = async () => {
    if (!rejectComment.trim()) {
      setError('Please provide a reason for rejection.');
      Swal.fire({
        icon: 'error',
        title: 'Missing Comment',
        text: 'Please provide a reason for rejecting the merge group.',
      });
      return;
    }

    if (!mergeGroup?.groupId) {
      setError('Invalid merge group ID.');
      Swal.fire({
        icon: 'error',
        title: 'Invalid Merge Group',
        text: 'Merge group ID is missing.',
      });
      return;
    }

    setError(null);
    try {
      const success = await handleRejectMerge(mergeGroup.groupId, rejectComment.trim());
      if (success) {
        setRejectComment('');
        onHide();
      }
    } catch (err: any) {
      const errorMsg = err.message || 'Failed to reject merge group.';
      setError(errorMsg);
      Swal.fire({
        icon: 'error',
        title: 'Merge Reject Error',
        text: errorMsg,
      });
    }
  };

  const { tripOne, tripTwo } = mergeGroup && Array.isArray(mergeGroup.bookings) ? splitTrips(mergeGroup.bookings) : { tripOne: [], tripTwo: [] };

  if (loadError) {
    return (
      <Modal
        show={show}
        onHide={onHide}
        size="xl"
        centered
        autoFocus={false}
        enforceFocus={false}
        aria-labelledby={`merge-group-${mergeGroup?.groupId || 'unknown'}`}
      >
        <Modal.Header closeButton>
          <Modal.Title id={`merge-group-${mergeGroup?.groupId || 'unknown'}`}>
            Merge Group Details - Group ID: {mergeGroup?.groupId || 'N/A'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-danger">Error loading Google Maps: {loadError.message}</div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      autoFocus={false}
      enforceFocus={false}
      aria-labelledby={`merge-group-${mergeGroup?.groupId || 'unknown'}`}
    >
      <Modal.Header closeButton>
        <Modal.Title id={`merge-group-${mergeGroup?.groupId || 'unknown'}`}>
          Merge Group Details - Group ID: {mergeGroup?.groupId || 'N/A'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {(isLoadingDrivers || isLoadingVehicles) && <Alert variant="info">Loading drivers and vehicles...</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}
        {!mergeGroup && <Alert variant="danger">No merge group data provided.</Alert>}
        {mergeGroup && (
          <div className="d-flex flex-wrap gap-2 align-items-end mb-3">
            <Form.Group style={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Form.Label>Allocated Vehicle</Form.Label>
              <Form.Select
                value={selectedVehicle}
                onChange={(e) => setSelectedVehicle(e.target.value)}
                disabled={isMerging || isLoadingVehicles}
                aria-label="Select vehicle for merge group"
              >
                <option value="">Select Vehicle</option>
                {vehicles.map((vehicle) => (
                  <option key={vehicle.numberPlate} value={vehicle.numberPlate}>
                    {vehicle.model} ({vehicle.numberPlate})
                  </option>
                ))}
              </Form.Select>
              {vehicles.length === 0 && !isLoadingVehicles && (
                <Form.Text className="text-danger">No vehicles available.</Form.Text>
              )}
            </Form.Group>

            <Form.Group style={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Form.Label>Allocated Driver</Form.Label>
              <Form.Select
                value={selectedDriver}
                onChange={(e) => setSelectedDriver(e.target.value)}
                disabled={isMerging || isLoadingDrivers}
                aria-label="Select driver for merge group"
              >
                <option value="">Select Driver</option>
                {drivers.map((driver) => (
                  <option key={driver.empNumber} value={driver.empNumber}>
                    {driver.name} ({driver.empNumber})
                  </option>
                ))}
              </Form.Select>
              {drivers.length === 0 && !isLoadingDrivers && (
                <Form.Text className="text-danger">No drivers available.</Form.Text>
              )}
            </Form.Group>

            <Form.Group style={{ flex: '1 1 300px', minWidth: '300px' }}>
              <Form.Label>Comments</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={rejectComment}
                onChange={(e) => setRejectComment(e.target.value)}
                placeholder="Enter reason for rejection (required for reject)"
                disabled={isMerging}
                aria-label="Rejection comment for merge group"
              />
            </Form.Group>

            <div className="d-flex gap-2">
              <Button
                variant="success"
                onClick={handleAccept}
                disabled={isMerging || isLoadingDrivers || isLoadingVehicles || !selectedVehicle || !selectedDriver}
                aria-label={`Accept merge group ${mergeGroup.groupId}`}
              >
                Approve
              </Button>
              <Button
                variant="danger"
                onClick={handleReject}
                disabled={isMerging || isLoadingDrivers || isLoadingVehicles || !rejectComment.trim()}
                aria-label={`Reject merge group ${mergeGroup.groupId}`}
              >
                Reject
              </Button>
            </div>
          </div>
        )}
        {mergeGroup && (
          <div className="row">
            <div className="col-md-6">
              <h5>Trip One</h5>
              {tripOne.length > 0 ? (
                <>
                  {tripOne.map((booking) => (
                    <div key={booking.id} className="mb-3">
                      <Table bordered hover>
                        <tbody>
                          <tr>
                            <td><strong>Booking ID</strong></td>
                            <td>{booking.id}</td>
                          </tr>
                          <tr>
                            <td><strong>User</strong></td>
                            <td>{booking.user?.name || 'N/A'} ({booking.user?.empNumber || 'N/A'})</td>
                          </tr>
                          <tr>
                            <td><strong>Status</strong></td>
                            <td>
                              <Badge bg={statusColors[booking.status] || 'secondary'}>
                                {booking.status || 'N/A'}
                              </Badge>
                            </td>
                          </tr>
                          <tr>
                            <td><strong>Departure</strong></td>
                            <td>{formatDate(booking.departureTime)}</td>
                          </tr>
                          <tr>
                            <td><strong>Arrival</strong></td>
                            <td>{formatDate(booking.arrivalTime)}</td>
                          </tr>
                          <tr>
                            <td><strong>Passengers</strong></td>
                            <td>{booking.noOfPassengers || 'N/A'}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  ))}
                  <div className="row">
                    <div className="col-md-6">
                      <h6>Destinations</h6>
                      <Table bordered hover>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Address</th>
                          </tr>
                        </thead>
                        <tbody>{renderDestinations(tripOne.flatMap((b) => b.locations || []))}</tbody>
                      </Table>
                    </div>
                    <div className="col-md-6">
                      <h6>Route Map</h6>
                      {isLoaded ? (
                        <GoogleMap
                          mapContainerStyle={{ height: '250px', width: '100%' }}
                          center={center}
                          zoom={12}
                          onLoad={(map) => {
                            tripOneMapRef.current = map;
                          }}
                        >
                          {tripOne
                            .flatMap((booking) => booking.locations || [])
                            .map((location, index) =>
                              location.lat && location.lng ? (
                                <Marker
                                  key={`${location.id}-${index}`}
                                  position={{ lat: parseFloat(location.lat), lng: parseFloat(location.lng) }}
                                  title={`Location ${index + 1}`}
                                />
                              ) : null
                            )}
                          {tripOneDirections && (
                            <DirectionsRenderer
                              directions={tripOneDirections}
                              options={{
                                polylineOptions: {
                                  strokeColor: '#1976D2',
                                  strokeOpacity: 0.8,
                                  strokeWeight: 5,
                                },
                              }}
                            />
                          )}
                        </GoogleMap>
                      ) : (
                        <div>Loading Google Maps...</div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <Alert variant="info">No bookings in Trip One.</Alert>
              )}
            </div>
            <div className="col-md-6">
              <h5>Trip Two</h5>
              {tripTwo.length > 0 ? (
                <>
                  {tripTwo.map((booking) => (
                    <div key={booking.id} className="mb-3">
                      <Table bordered hover>
                        <tbody>
                          <tr>
                            <td><strong>Booking ID</strong></td>
                            <td>{booking.id}</td>
                          </tr>
                          <tr>
                            <td><strong>User</strong></td>
                            <td>{booking.user?.name || 'N/A'} ({booking.user?.empNumber || 'N/A'})</td>
                          </tr>
                          <tr>
                            <td><strong>Status</strong></td>
                            <td>
                              <Badge bg={statusColors[booking.status] || 'secondary'}>
                                {booking.status || 'N/A'}
                              </Badge>
                            </td>
                          </tr>
                          <tr>
                            <td><strong>Departure</strong></td>
                            <td>{formatDate(booking.departureTime)}</td>
                          </tr>
                          <tr>
                            <td><strong>Arrival</strong></td>
                            <td>{formatDate(booking.arrivalTime)}</td>
                          </tr>
                          <tr>
                            <td><strong>Passengers</strong></td>
                            <td>{booking.noOfPassengers || 'N/A'}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>
                  ))}
                  <div className="row">
                    <div className="col-md-6">
                      <h6>Destinations</h6>
                      <Table bordered hover>
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Address</th>
                          </tr>
                        </thead>
                        <tbody>{renderDestinations(tripTwo.flatMap((b) => b.locations || []))}</tbody>
                      </Table>
                    </div>
                    <div className="col-md-6">
                      <h6>Route Map</h6>
                      {isLoaded ? (
                        <GoogleMap
                          mapContainerStyle={{ height: '250px', width: '100%' }}
                          center={center}
                          zoom={12}
                          onLoad={(map) => {
                            tripTwoMapRef.current = map;
                          }}
                        >
                          {tripTwo
                            .flatMap((booking) => booking.locations || [])
                            .map((location, index) =>
                              location.lat && location.lng ? (
                                <Marker
                                  key={`${location.id}-${index}`}
                                  position={{ lat: parseFloat(location.lat), lng: parseFloat(location.lng) }}
                                  title={`Location ${index + 1}`}
                                />
                              ) : null
                            )}
                          {tripTwoDirections && (
                            <DirectionsRenderer
                              directions={tripTwoDirections}
                              options={{
                                polylineOptions: {
                                  strokeColor: '#1976D2',
                                  strokeOpacity: 0.8,
                                  strokeWeight: 5,
                                },
                              }}
                            />
                          )}
                        </GoogleMap>
                      ) : (
                        <div>Loading Google Maps...</div>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <Alert variant="info">No bookings in Trip Two.</Alert>
              )}
            </div>
          </div>
        )}
        {mergeGroup && (
          <>
            <div className="mt-3">
              <strong>Total Estimated KMs:</strong> {getTotalEstimatedKMs(mergeGroup.bookings).toFixed(2)} km
            </div>
            <div className="mt-3">
              <strong>Total Passengers:</strong> {getTotalPassengers(mergeGroup.bookings)}
            </div>
            <div className="mt-3">
              <strong>Suggested Departure Time:</strong>{' '}
              {mergeGroup.suggestedDepartureTime ? formatDate(Number(mergeGroup.suggestedDepartureTime)) : 'N/A'}
            </div>
            <div className="mt-3">
              <strong>Suggested Arrival Time:</strong>{' '}
              {mergeGroup.suggestedArrivalTime ? formatDate(Number(mergeGroup.suggestedArrivalTime)) : 'N/A'}
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          onClick={onHide}
          disabled={isMerging || isLoadingDrivers || isLoadingVehicles}
          aria-label="Close merge group modal"
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ViewMergeBooking;